import React, { useState, useEffect, useMemo } from "react";
import { Modal, Form, Input, Select, Button, TimePicker, Typography } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const AdminEditRequestModal = ({ 
  open, 
  onCancel, 
  passengerData, 
  onSave, 
  isMultiplePickupAllowed, 
  showAllFields = false,
  tripStartTime = null,
  tripArrivalTime = null
}) => {
  const [form] = Form.useForm();
  const [isDirty, setIsDirty] = useState(false);
  const [loading, setLoading] = useState(false);

  const hasEmployeeId = !!(passengerData?.employee_id || passengerData?.pivot?.employee_id);
  const isEmployeeObject = !hasEmployeeId && !passengerData?.trip_request_id && typeof passengerData?.id === 'number';
  const isActuallyEmployee = hasEmployeeId || isEmployeeObject;
  const isRequestor = !!(passengerData?.is_requestor || passengerData?.pivot?.is_requestor);

  const isMultipleAllowed = useMemo(() => {
    if (showAllFields) return true;
    
    // Priority 1: Explicitly passed prop
    if (isMultiplePickupAllowed !== undefined && isMultiplePickupAllowed !== null) {
      return isMultiplePickupAllowed === true || String(isMultiplePickupAllowed) === "1" || Number(isMultiplePickupAllowed) === 1;
    }

    // Priority 2: Inferred from passenger data
    const tripData = passengerData?.trip || passengerData;
    let val = tripData?.allowed_multiple_pickup;
    if (val === undefined || val === null) {
      val = tripData?.isMultiplePickupAllowed;
    }
    
    if (val !== undefined && val !== null) {
      return val === true || String(val) === "1" || Number(val) === 1 || String(val) === "true";
    }

    return true; // Default to true if absolutely no info found
  }, [showAllFields, isMultiplePickupAllowed, passengerData]);

  useEffect(() => {
    if (open && passengerData) {
      // Robust resolution of values from nested or flat structures
      const status = passengerData.request_status || passengerData.status || passengerData.pivot?.request_status || "Approved by AO";
      const pickupTime = passengerData.estimated_pickup_time || passengerData.pivot?.estimated_pickup_time;
      const pickupVenue = passengerData.pickup_venue || passengerData.pivot?.pickup_venue;
      const remarks = passengerData.remarks ?? passengerData.pivot?.remarks ?? "";

      // Robust time parsing for the TimePicker
      const timeFormats = ["HH:mm:ss", "HH:mm", "h:mm a", "H:mm:ss", "H:mm", "hh:mm a", "h:mm A"];
      const parsedTime = pickupTime ? dayjs(pickupTime, timeFormats) : null;

      form.setFieldsValue({
        pickupTime: (parsedTime && parsedTime.isValid()) ? parsedTime : null,
        pickupVenue: pickupVenue || "",
        status: status,
        remarks: remarks,
        externalName: passengerData.external_passenger_name || passengerData.name || (passengerData.first_name ? `${passengerData.first_name} ${passengerData.last_name || ""}`.trim() : ""),
        externalEmail: passengerData.external_passenger_email || passengerData.email || "",
      });
    }
  }, [open, passengerData, form]);

  const baseline = useMemo(() => {
    if (!passengerData) return null;
    const initialStatus = passengerData.request_status || passengerData.status || passengerData.pivot?.request_status || "Approved by AO";
    const initialPickupTime = passengerData.estimated_pickup_time || passengerData.pivot?.estimated_pickup_time;
    const initialPickupVenue = passengerData.pickup_venue || passengerData.pivot?.pickup_venue;
    const initialRemarks = passengerData.remarks ?? passengerData.pivot?.remarks ?? "";
    const initialName = passengerData.external_passenger_name || passengerData.name || (passengerData.first_name ? `${passengerData.first_name} ${passengerData.last_name || ""}`.trim() : ""),
    initialEmail = passengerData.external_passenger_email || passengerData.email || "";

    const timeFormats = ["HH:mm:ss", "HH:mm", "h:mm a", "H:mm:ss", "H:mm", "hh:mm a", "h:mm A"];
    const pTimeInit = initialPickupTime ? dayjs(initialPickupTime, timeFormats).format("HH:mm:ss") : null;

    return {
      status: initialStatus,
      pickupTime: pTimeInit,
      pickupVenue: (initialPickupVenue || "").toString().trim(),
      remarks: (initialRemarks || "").toString().trim(),
      externalName: (initialName || "").toString().trim(),
      externalEmail: (initialEmail || "").toString().trim()
    };
  }, [passengerData]);

  useEffect(() => {
    if (!open) {
      setIsDirty(false);
    }
  }, [open]);

  const handleValuesChange = (_, allValues) => {
    if (!baseline) return;
    
    const pTimeCurrent = allValues.pickupTime ? allValues.pickupTime.format("HH:mm:ss") : null;
    
    const vStatus = allValues.status || baseline.status;
    const vPickupVenue = (allValues.pickupVenue || "").toString().trim();
    const vRemarks = (allValues.remarks || "").toString().trim();
    const vName = (allValues.externalName || "").toString().trim();
    const vEmail = (allValues.externalEmail || "").toString().trim();

    const hasChanged = 
      vStatus !== baseline.status ||
      (isMultipleAllowed && pTimeCurrent !== baseline.pickupTime) ||
      (isMultipleAllowed && vPickupVenue !== baseline.pickupVenue) ||
      vRemarks !== baseline.remarks ||
      (!isActuallyEmployee && (
        vName !== baseline.externalName ||
        vEmail !== baseline.externalEmail
      ));

    setIsDirty(hasChanged);
  };

  if (!passengerData) return null;

  // Resolved trip boundaries for validation
  const tStart = tripStartTime || passengerData.trip?.pickup_time || passengerData.trip?.time;
  const tArrival = tripArrivalTime || passengerData.trip?.estimated_arrival_time || passengerData.trip?.estimatedArrival;

  const disabledTime = () => {
    const tripStart = dayjs(tStart, ["HH:mm:ss", "h:mm a", "H:mm:ss", "HH:mm"]);
    const tripEnd = dayjs(tArrival, ["HH:mm:ss", "h:mm a", "H:mm:ss", "HH:mm"]);
    
    if (!tripStart.isValid() || !tripEnd.isValid()) return {};

    return {
      disabledHours: () => {
        const hours = [];
        for (let i = 0; i < 24; i++) {
          if (i < tripStart.hour() || i > tripEnd.hour()) hours.push(i);
        }
        return hours;
      },
      disabledMinutes: (h) => {
        const minutes = [];
        if (h === tripStart.hour()) {
          for (let i = 0; i < tripStart.minute(); i++) minutes.push(i);
        }
        if (h === tripEnd.hour()) {
          for (let i = tripEnd.minute() + 1; i < 60; i++) minutes.push(i);
        }
        return minutes;
      }
    };
  };

  const modalTitle = (isRequestor || isActuallyEmployee) ? "Edit Passenger Status" : "Edit Guest Status";

  const sanitizeInput = (val) => {
    if (typeof val !== 'string') return val;
    return val.trim().replace(/\s+/g, ' ');
  };

  const handleSubmit = async (values) => {
    const payload = {
      id: passengerData.id,
      trip_request_id: passengerData.trip_request_id,
      request_status: values.status,
      remarks: values.remarks === "" ? null : (sanitizeInput(values.remarks) || null),
      external_passenger_name: sanitizeInput(values.externalName) || null,
      external_passenger_email: sanitizeInput(values.externalEmail) || null,
    };

    // Only include custom logistics if multiple pickups are allowed for this trip
    if (isMultipleAllowed) {
      payload.estimated_pickup_time = values.pickupTime ? values.pickupTime.format("HH:mm:ss") : null;
      payload.pickup_venue = sanitizeInput(values.pickupVenue) || null;
    }
    
    setLoading(true);
    try {
      await onSave(payload);
      form.resetFields();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      title={<Title level={4} style={{ margin: 0, color: 'white' }}>{modalTitle}</Title>}
      className="ouber-modal white-close-icon"
      width={400}
      zIndex={1070}
      closable={!loading}
    >
      <div style={{ marginTop: '20px' }}>
        <Form form={form} layout="vertical" onFinish={handleSubmit} onValuesChange={handleValuesChange}>
          {!isActuallyEmployee && (
            <>
              <Form.Item name="externalName" label="Full Name" rules={[{ required: true, whitespace: true, message: "Input cannot be empty" }]}>
                <Input placeholder="Enter full name" />
              </Form.Item>
              
              <Form.Item name="externalEmail" label="Email Address" rules={[{ required: true, type: 'email', message: 'Input cannot be empty' }]}>
                <Input placeholder="Enter email" />
              </Form.Item>
            </>
          )}

          {isMultipleAllowed && (
            <>
              <Form.Item name="pickupTime" label="Pickup Time">
                <TimePicker use12Hours format="h:mm a" style={{ width: '100%' }} disabledTime={disabledTime} />
              </Form.Item>

              <Form.Item name="pickupVenue" label="Pickup Venue" rules={[{ whitespace: true, message: "Input cannot be empty" }]}>
                <Input placeholder="e.g. UPLB Gate" />
              </Form.Item>
            </>
          )}

          {(passengerData.trip_id || passengerData.trip || passengerData.pivot?.trip_id) && (
            <Form.Item name="status" label="Passenger Status" rules={[{ required: true, message: "Input cannot be empty" }]}>
              <Select>
                <Option value="Approved by AO">Approved by AO</Option>
                <Option value="Requested to Join">Requested to Join</Option>
                <Option value="Declined by AO">Declined by AO</Option>
              </Select>
            </Form.Item>
          )}

          <Form.Item name="remarks" label="Remarks" rules={[{ whitespace: true, message: "Input cannot be empty" }]}>
            <TextArea rows={3} placeholder="Add remarks..." />
          </Form.Item>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "24px" }}>
            <Button onClick={onCancel} disabled={loading}>Cancel</Button>
            <Button type="primary" htmlType="submit" className="ouber-btn-forest" disabled={!isDirty} loading={loading}>
              Confirm Edit
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default AdminEditRequestModal;
